import React, { useContext, useState } from 'react';
import { Box, Button, List, ListItem, ListItemButton, styled, IconButton, useTheme, Typography, Divider, MenuItem, Select, FormControl } from '@mui/material'
import { Delete, Sort, TrendingUp, TrendingDown, Schedule } from '@mui/icons-material';
import { TextSizeContext } from 'data/index'
import { useTranslation } from 'react-i18next'
import { TextSizes } from 'entities/constants'
import { ParagraphInterface, SkillboxCourseInterface } from 'entities/interfaces'
import SkillboxListItem from 'ui/components/mobile/skillbox/SkillboxListItem'
import ImageComponent from 'ui/components/shared/ImageComponent';

const SkillboxMobileWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
}))

const HeaderSection = styled(Box)({
  padding: '1.25rem 1.5rem 1rem 1.5rem',
  paddingTop: '5.5rem',
  background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.95) 50%, rgba(161, 183, 165, 0.1) 100%)',
  borderBottom: '1px solid rgba(161, 183, 165, 0.2)',
  boxShadow: '0 2px 16px rgba(161, 183, 165, 0.1)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
    backdropFilter: 'blur(20px)',
    zIndex: -1
  },
  
  '@media (max-width: 390px)': {
    padding: '1rem 1.25rem 0.75rem 1.25rem',
    paddingTop: '5rem',
  },
  
  '@media (max-width: 375px)': {
    padding: '0.875rem 1rem 0.625rem 1rem',
    paddingTop: '4.5rem',
  },
  
  '@media (max-width: 320px)': {
    padding: '0.75rem 0.875rem 0.5rem 0.875rem',
    paddingTop: '4rem',
  },
  
  '@media (max-width: 280px)': {
    padding: '0.5rem 0.75rem 0.375rem 0.75rem',
    paddingTop: '3.5rem',
  }
});

// Moderner Titel wie in SkillsListView
const ModernTitle = styled(Typography)({
  fontSize: '2.2rem',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #A1B7A5 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: '1.25rem',
  letterSpacing: '-0.025em',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  
  '@media (max-width: 390px)': {
    fontSize: '2rem',
    marginBottom: '1rem'
  },
  
  '@media (max-width: 375px)': {
    fontSize: '1.8rem',
    marginBottom: '0.875rem'
  },
  
  '@media (max-width: 320px)': {
    fontSize: '1.6rem',
    marginBottom: '0.75rem',
    letterSpacing: '-0.02em'
  },
  
  '@media (max-width: 280px)': {
    fontSize: '1.4rem',
    marginBottom: '0.625rem',
    letterSpacing: '-0.015em'
  }
});

// Moderne Sortierfunktion
const SortContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  width: '100%',
});

const SortLabel = styled(Typography)({
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#4b5563',
  minWidth: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  
  '@media (max-width: 375px)': {
    fontSize: '0.875rem'
  }
});

const StyledFormControl = styled(FormControl)({
  flexGrow: 1,
  
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f8f9fa',
    borderRadius: '2rem',
    fontSize: '0.95rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(161, 183, 165, 0.2)',
    minHeight: '3rem',
    
    '& fieldset': {
      border: 'none'
    },
    
    '&:hover': {
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 16px rgba(161, 183, 165, 0.15)',
      transform: 'translateY(-1px)'
    },
    
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0 6px 24px rgba(161, 183, 165, 0.2)',
      transform: 'translateY(-2px)',
      borderColor: 'rgba(161, 183, 165, 0.4)'
    }
  },
  
  '& .MuiSelect-select': {
    padding: '0.875rem 1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    
    '@media (max-width: 360px)': {
      padding: '0.75rem 1rem',
      fontSize: '0.9rem'
    }
  },
  
  '& .MuiMenuItem-root': {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    
    '&:hover': {
      backgroundColor: 'rgba(161, 183, 165, 0.1)'
    },
    
    '&.Mui-selected': {
      backgroundColor: 'rgba(161, 183, 165, 0.15)',
      '&:hover': {
        backgroundColor: 'rgba(161, 183, 165, 0.2)'
      }
    }
  }
});

// Kursbilder-Komponenten wie in SkillsListView
const ProfileImageContainer = styled(Box)({
  width: '3.75rem',
  height: '3.75rem',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundColor: '#e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid rgba(161, 183, 165, 0.15)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: 'rgba(161, 183, 165, 0.3)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
  },
  
  '@media (max-width: 390px)': {
    width: '3.5rem',
    height: '3.5rem'
  },
  
  '@media (max-width: 375px)': {
    width: '3.25rem',
    height: '3.25rem'
  },
  
  '@media (max-width: 320px)': {
    width: '3rem',
    height: '3rem',
    border: '1.5px solid rgba(161, 183, 165, 0.15)'
  },
  
  '@media (max-width: 280px)': {
    width: '2.75rem',
    height: '2.75rem',
    border: '1px solid rgba(161, 183, 165, 0.15)'
  }
});

const StyledList = styled(List)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
    padding: 0,
    flexGrow: 1,
    height: '100vh',
}))

type SortOption = 'progress-desc' | 'progress-asc' | 'alphabetical';

const SkillboxListView = ({ skills, addUserCourse, deleteUserCourse, setChapterNote,
    getChapterNote, getChapterHistory, getChapterMaterial }: {
        skills: SkillboxCourseInterface[],
        addUserCourse: (courseId: number) => Promise<number>,
        deleteUserCourse: (courseId: number) => Promise<number>
        setChapterNote: (chapterId: number, note: string) => Promise<void>;
        getChapterNote: (chapterId: number) => Promise<string>
        getChapterHistory: (chapterId: number) => Promise<ParagraphInterface[]>
        getChapterMaterial: (chapterId: number) => void
    }) => {

    const { t } = useTranslation()
    const { state: textSize } = useContext(TextSizeContext)
    const [moreOptionLabel, setMoreOptionLabel] = useState('')
    const [deleteConfirmationModal, setDeleteConfirmationModal] = useState<number | false>(false);
    const [swipedItem, setSwipedItem] = useState<number | null>(null);
    const [sortOption, setSortOption] = useState<SortOption>('progress-desc');

    const theme = useTheme();
    
    const handleMore = (label: string) => {
        setMoreOptionLabel(moreOptionLabel !== label ? label : '')
    }

    const handleSwipe = (skillId: number) => {
        setSwipedItem(swipedItem === skillId ? null : skillId);
    };

    const handleDelete = (skillId: number) => {
        deleteUserCourse(skillId);
        setSwipedItem(null);
    };

    // Sortierfunktion
    const sortSkills = (skills: SkillboxCourseInterface[], option: SortOption) => {
        const sortedSkills = [...skills];
        
        switch (option) {
            case 'progress-desc':
                return sortedSkills.sort((a, b) => (b.progress || 0) - (a.progress || 0));
            case 'progress-asc':
                return sortedSkills.sort((a, b) => (a.progress || 0) - (b.progress || 0));
            case 'alphabetical':
                return sortedSkills.sort((a, b) => a.title.localeCompare(b.title));

            default:
                return sortedSkills;
        }
    };

    const sortedSkills = sortSkills(skills, sortOption);

    // Aktualisiere die Translation-Datei
    const getSortIcon = (option: SortOption) => {
        switch (option) {
            case 'progress-desc':
                return <TrendingUp sx={{ fontSize: '1.1rem', color: '#10b981' }} />;
            case 'progress-asc':
                return <TrendingDown sx={{ fontSize: '1.1rem', color: '#f59e0b' }} />;
            case 'alphabetical':
                return <Sort sx={{ fontSize: '1.1rem', color: '#6366f1' }} />;
            default:
                return <Sort sx={{ fontSize: '1.1rem' }} />;
        }
    };

    return (
        <SkillboxMobileWrapper>
            <HeaderSection>
                <ModernTitle>{t('Skillbox')}</ModernTitle>
                
                <SortContainer>
                    <StyledFormControl size="small">
                        <Select
                            value={sortOption}
                            onChange={(e) => setSortOption(e.target.value as SortOption)}
                            displayEmpty
                            renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {getSortIcon(value as SortOption)}
                                    <span>
                                        {value === 'progress-desc' && t('Fortschritt (Hoch → Niedrig)')}
                                        {value === 'progress-asc' && t('Fortschritt (Niedrig → Hoch)')}
                                        {value === 'alphabetical' && t('Alphabetisch')}
                                    </span>
                                </Box>
                            )}
                        >
                            <MenuItem value="progress-desc">
                                <TrendingUp sx={{ fontSize: '1.1rem', color: '#10b981' }} />
                                <span>{t('Fortschritt (Hoch → Niedrig)')}</span>
                            </MenuItem>
                            <MenuItem value="progress-asc">
                                <TrendingDown sx={{ fontSize: '1.1rem', color: '#f59e0b' }} />
                                <span>{t('Fortschritt (Niedrig → Hoch)')}</span>
                            </MenuItem>
                            <MenuItem value="alphabetical">
                                <Sort sx={{ fontSize: '1.1rem', color: '#6366f1' }} />
                                <span>{t('Alphabetisch')}</span>
                            </MenuItem>

                        </Select>
                    </StyledFormControl>
                </SortContainer>
            </HeaderSection>
            
            <StyledList>
                {sortedSkills.map((skill) => (
                    <SkillboxListItem
                        key={skill.id}
                        skill={skill}
                        addUserCourse={addUserCourse}
                        deleteUserCourse={deleteUserCourse}
                        setChapterNote={setChapterNote}
                        getChapterNote={getChapterNote}
                        getChapterHistory={getChapterHistory}
                        getChapterMaterial={getChapterMaterial}
                    />
                ))}
            </StyledList>
        </SkillboxMobileWrapper>
    )
}

export default SkillboxListView